import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useGameStore } from '../../stores/gameStore';
import { applyDTODelta, FeatureType, getMaxHP, getMaxMP, RestSummaryDTO } from '@hexcrawl/shared-utils';
import { gameService } from '../../services/gameService';
import { RestResultOverlay } from './RestResultOverlay';
import { encounterService } from '../../services/encounterService';
import { useEncounterStore } from '../../stores/encounterStore';
import { launchCombatScene } from '../../services/combatService';
import type { AppliedEffectDTO, EncounterDTO } from '@hexcrawl/shared-utils';
import { CharacterPanel } from '../character/CharacterPanel';
import { LoreModal } from '../character/LoreModal';
import { QuestJournal } from './QuestJournal';
import { useDiscoveryStore } from '../../stores/discoveryStore';
import { useQuestStore } from '../../stores/questStore';
import { useNpcStore } from '../../stores/npcStore';
import { useUiStore } from '../../stores/uiStore';


export const MapHUD: React.FC = () => {
  const gameState = useGameStore((s) => s.gameState);
  const gameConfig = useGameStore((s) => s.gameConfig);
  const setGameState = useGameStore((s) => s.setGameState);
  const [resting, setResting] = useState(false);
  const [restResult, setRestResult] = useState<RestSummaryDTO | null>(null);
  const [restError, setRestError] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [loreOpen, setLoreOpen] = useState(false);
  const pendingLoreId = useDiscoveryStore((s) => s.pendingLoreId);
  const clearPendingLoreId = useDiscoveryStore((s) => s.clearPendingLoreId);

  useEffect(() => {
    if (pendingLoreId !== null) setLoreOpen(true);
  }, [pendingLoreId]);

  // Aiming a consumable at a hex needs the map visible, so raising the mode closes the
  // character sheet the item was picked from.
  const hexTargetItem = useUiStore((s) => s.hexTargetItem);
  const cancelHexTargeting = useUiStore((s) => s.cancelHexTargeting);
  useEffect(() => {
    if (hexTargetItem) setPanelOpen(false);
  }, [hexTargetItem]);
  const quests = useQuestStore((s) => s.quests);
  const badgePulsing = useQuestStore((s) => s.badgePulsing);
  const setJournalOpen = useQuestStore((s) => s.setJournalOpen);
  const journalOpen = useQuestStore((s) => s.journalOpen);
  const activeQuestCount = quests.filter(q => q.status === 'active' || q.status === 'ready_to_complete').length;
  const pendingEncounterRef = useRef<EncounterDTO | null>(null);
  const pendingEncounterEffectsRef = useRef<AppliedEffectDTO[] | null>(null);

  const currentHex = useMemo(() => {
    if (!gameState?.map) return null;
    return gameState.map.hexes.find(h => h.x === gameState.location.x && h.y === gameState.location.y) ?? null;
  }, [gameState?.map, gameState?.location.x, gameState?.location.y]);

  const discoveredPlaces = useDiscoveryStore((s) => s.places);
  const regionName = useMemo(() => {
    if (!gameState) return null;
    const px = gameState.location.x;
    const py = gameState.location.y;
    for (const place of discoveredPlaces) {
      const loc = place.location;
      if (loc.type === 'hex' && loc.x === px && loc.y === py) return place.name;
      if (loc.type === 'region' && loc.hexes.some(h => h.x === px && h.y === py)) return place.name;
    }
    return null;
  }, [discoveredPlaces, gameState?.location.x, gameState?.location.y]);

  if (!gameState || !gameState.character) return null;

  const character = gameState.character;
  const maxHP = getMaxHP(character);
  const maxMP = getMaxMP(character);

  const hasInn = currentHex != null && (currentHex.feature === FeatureType.TOWN || currentHex.feature === FeatureType.CASTLE);
  const innCost = gameConfig?.innCost ?? 5;
  const canAffordInn = hasInn && gameState.gold >= innCost;

  const handleRest = async () => {
    setResting(true);
    setRestResult(null);
    setRestError(null);
    try {
      const response = await gameService.rest({ gameId: gameState.id });
      const newState = applyDTODelta(gameState, response.stateDelta);
      setGameState(newState);
      setRestResult(response.restSummary);

      if (response.restSummary.hirelingHeals?.length) {
        const npcs = useNpcStore.getState().npcs;
        for (const heal of response.restSummary.hirelingHeals) {
          const npc = npcs[heal.npcId];
          if (npc) {
            useNpcStore.getState().setNpc({
              ...npc,
              currentHP: (npc.currentHP ?? 0) + heal.hpRestored,
              currentMP: (npc.currentMP ?? 0) + heal.mpRestored,
            });
          }
        }
      }

      if (response.encounter) {
        pendingEncounterRef.current = response.encounter;
        pendingEncounterEffectsRef.current = response.appliedEffects ?? null;
      }
      if (response.combat) {
        const updatedState = { ...newState, activeCombat: response.combat };
        setGameState(updatedState);
        try { window.phaserGame?.scene?.stop('MapScene'); } catch {}
        await launchCombatScene(updatedState);
      }
    } catch (err: any) {
      setRestError(err.message || 'Failed to rest.');
      setTimeout(() => setRestError(null), 3000);
    } finally {
      setResting(false);
    }
  };

  const noMoves = gameState.movePoints === 0;

  // Determine button label and style
  let buttonLabel: string;
  let buttonStyle: React.CSSProperties;

  if (canAffordInn) {
    buttonLabel = `Stay at Inn (${innCost}g)`;
    buttonStyle = {
      background: noMoves ? '#5a4a00' : '#3a2a1a',
      color: '#ffd700',
      border: '1px solid #ffd700',
      borderRadius: 6,
      padding: '4px 10px',
      fontFamily: 'monospace',
      fontSize: 13,
      cursor: resting ? 'wait' : 'pointer',
      pointerEvents: 'auto',
      opacity: resting ? 0.6 : 1,
    };
  } else {
    buttonLabel = 'Make Camp';
    buttonStyle = {
      background: noMoves ? '#5a3a00' : '#1a3a5a',
      color: noMoves ? '#ffcc66' : '#a8d1ff',
      border: noMoves ? '1px solid #ffcc66' : '1px solid #5aa0ff',
      borderRadius: 6,
      padding: '4px 10px',
      fontFamily: 'monospace',
      fontSize: 13,
      cursor: resting ? 'wait' : 'pointer',
      pointerEvents: 'auto',
      opacity: resting ? 0.6 : 1,
    };
  }

  return (
    <>
      {/* Top-left column — exit + character panel */}
      <div style={{
        position: 'fixed',
        top: 14,
        left: 14,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 6,
        pointerEvents: 'auto',
      }}>
        <button
          onClick={() => { window.location.href = import.meta.env.VITE_SITE_URL ?? '/'; }}
          title="Return to HexCrawl site"
          style={{
            background: 'rgba(14, 34, 48, 0.85)',
            border: '1px solid #3a5a7a',
            borderRadius: 6,
            color: '#6699bb',
            fontFamily: 'monospace',
            fontSize: 12,
            padding: '5px 10px',
            cursor: 'pointer',
          }}
        >
          ← Exit
        </button>
        <button
          onClick={() => setPanelOpen((v) => !v)}
          title="Character & Inventory"
          style={{
            width: 64,
            height: 64,
            padding: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            opacity: 1,
          }}
        >
          <div style={{
            width: 56,
            height: 56,
            backgroundImage: 'url(/assets/images/character-sheet.png)',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }} />
        </button>
        <button
          onClick={() => setLoreOpen((v) => !v)}
          title="Lore"
          style={{
            width: 64,
            height: 64,
            padding: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            opacity: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src="/assets/images/legendaries.png"
            alt="Lore"
            style={{ width: 56, height: 56, imageRendering: 'pixelated' }}
          />
        </button>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setJournalOpen(!journalOpen)}
            title="Quest Journal"
            style={{
              width: 64,
              height: 64,
              padding: 0,
              background: journalOpen ? 'rgba(90, 160, 255, 0.15)' : 'none',
              border: journalOpen ? '1px solid #5aa0ff' : 'none',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src="/assets/images/quests.png"
              alt="Quest Journal"
              style={{ width: 56, height: 56, imageRendering: 'pixelated' }}
            />
          </button>
          {activeQuestCount > 0 && (
            <div
              className={badgePulsing ? 'quest-badge quest-badge--pulse' : 'quest-badge'}
            >
              {activeQuestCount}
            </div>
          )}
        </div>
      </div>

      {panelOpen && character && (
        <CharacterPanel character={character} onClose={() => setPanelOpen(false)} />
      )}
      {hexTargetItem && (
        <div style={{
          position: 'fixed',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 14px',
          background: 'rgba(14, 34, 48, 0.95)',
          border: '2px solid #cc99ff',
          borderRadius: 10,
          color: '#e6d5ff',
          fontSize: 14,
          fontFamily: 'monospace',
          zIndex: 1200,
          pointerEvents: 'auto',
        }}>
          <span>{hexTargetItem.itemName}: click a hex to travel there.</span>
          <button
            onClick={cancelHexTargeting}
            style={{
              background: '#2a1a3a',
              color: '#e6d5ff',
              border: '1px solid #cc99ff',
              borderRadius: 6,
              padding: '3px 10px',
              fontFamily: 'monospace',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Cancel (Esc)
          </button>
        </div>
      )}
      {loreOpen && <LoreModal initialExpandedId={pendingLoreId ?? undefined} onClose={() => { setLoreOpen(false); clearPendingLoreId(); }} />}
      {journalOpen && <QuestJournal quests={quests} onClose={() => setJournalOpen(false)} />}

      <div style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        padding: '8px 12px',
        background: 'rgba(14, 34, 48, 0.9)',
        border: '2px solid #5aa0ff',
        borderRadius: 10,
        color: '#a8d1ff',
        fontSize: 14,
        fontFamily: 'monospace',
        zIndex: 1000,
        userSelect: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: 'calc(100vw - 32px)',
      }}>
        <StatGroup>
          <Stat label="HP" value={character.currentHP} max={maxHP} color="#88ff88" />
          <Stat label="MP" value={character.currentMP} max={maxMP} color="#88ccff" />
        </StatGroup>
        <StatGroup>
          <Stat label="Food" value={gameState.food} color="#ffcc66" />
          <Stat label="Gold" value={gameState.gold} color="#ffd700" />
        </StatGroup>
        <StatGroup>
          <Stat label="Day" value={gameState.day} max={gameState.maxDays} color={gameState.day >= gameState.maxDays ? '#ff6666' : '#cccccc'} />
          <Stat label="Moves" value={gameState.movePoints} color="#cc99ff" />
        </StatGroup>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          <button
            onClick={handleRest}
            disabled={resting}
            style={buttonStyle}
          >
            {buttonLabel}
          </button>
          {hasInn && !canAffordInn && (
            <span style={{ color: '#886644', fontSize: 10 }}>Inn: {innCost}g (not enough)</span>
          )}
        </div>
        {restError && (
          <span style={{ color: '#ff8866', fontSize: 12 }}>{restError}</span>
        )}
      </div>
      {regionName && (
        <div style={{
          position: 'fixed',
          top: 14,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(14, 34, 48, 0.88)',
          border: '1px solid #5aa0ff',
          borderRadius: 8,
          padding: '5px 14px',
          color: '#d9c8a0',
          fontSize: 12,
          fontFamily: 'monospace',
          letterSpacing: 0.5,
          zIndex: 1000,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}>
          You are in {regionName}
        </div>
      )}

      {restResult && (
        <RestResultOverlay summary={restResult} onDismiss={async () => {
          setRestResult(null);
          const pending = pendingEncounterRef.current;
          const pendingEffects = pendingEncounterEffectsRef.current;
          pendingEncounterRef.current = null;
          pendingEncounterEffectsRef.current = null;
          if (pending) {
            try {
              const response = await encounterService.updateNode(pending.id, null);
              useEncounterStore.getState().setActiveEncounter(response.encounter, response.node, pending.npcName, pendingEffects ?? undefined);
            } catch (err) {
              console.error('Failed to load encounter def:', err);
            }
          }
        }} />
      )}
    </>
  );
};

const Stat: React.FC<{
  label: string;
  value: number;
  max?: number;
  color: string;
}> = ({ label, value, max, color }) => (
  <div style={{ display: 'flex', gap: 4, alignItems: 'baseline' }}>
    <span style={{ color: '#7799bb', fontSize: 12 }}>{label}</span>
    <span style={{ color, fontWeight: 'bold' }}>
      {value}{max !== undefined ? `/${max}` : ''}
    </span>
  </div>
);

const StatGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    display: 'flex',
    gap: 8,
    alignItems: 'baseline',
    padding: '2px 8px',
    borderRight: '1px solid #3a5a7a',
  }}>
    {children}
  </div>
);
